import { useParams, useNavigate } from "react-router-dom"
import { useCandidateDetail } from "../hooks/useCandidateDetail"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { useApplications } from "@/modules/recruitment/hooks/useApplications"
import { parseApplicationStage } from "@/modules/recruitment/applications/dtos/ApplicationDetails"
import {
  getApplicationStageLabel,
  getApplicationStageBadgeVariant,
} from "@/modules/recruitment/applications/enums/CandidateStatus"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  ArrowLeft,
  Clock,
  User,
} from "lucide-react"

export const CandidateDetailPage = () => {
  const { candidateId } = useParams<{ candidateId: string }>()
  const navigate = useNavigate()
  const { candidate, timeline, isLoading, error } = useCandidateDetail(
    candidateId || "",
  )
  const { data: allApplications = [] } = useApplications()

  // Filter applications for this candidate
  const candidateApplications = allApplications.filter(
    (app) => app.candidateId === candidateId,
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner />
      </div>
    )
  }

  if (error || !candidate) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-lg text-muted-foreground">
          Failed to load candidate details
        </p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {candidate.firstName} {candidate.lastName}
            </h1>
            <p className="text-muted-foreground mt-1">{candidate.email}</p>
          </div>
          {/* Show application stages */}
          <div className="flex gap-2 flex-wrap">
            {candidateApplications.length > 0 ? (
              candidateApplications.map((app) => {
                const stage = parseApplicationStage(app.currentStage)
                return (
                  <Badge
                    key={app.id}
                    variant={getApplicationStageBadgeVariant(stage)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() =>
                      navigate(`/recruitment/applications/${app.id}`)
                    }
                  >
                    {getApplicationStageLabel(stage)}
                  </Badge>
                )
              })
            ) : (
              <Badge variant="secondary">No Applications</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <a
                    href={`mailto:${candidate.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {candidate.email}
                  </a>
                </div>
              </div>

              {candidate.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <a
                      href={`tel:${candidate.phone}`}
                      className="text-blue-600 hover:underline"
                    >
                      {candidate.phone}
                    </a>
                  </div>
                </div>
              )}

              {candidate.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{candidate.location}</p>
                  </div>
                </div>
              )}

              {candidate.cvUrl && (
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Resume/CV</p>
                    <a
                      href={candidate.cvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Document
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Skills Card */}
          {candidate.skills && candidate.skills.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Skills & Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {candidate.skills.map((skill, index) => (
                    <Badge key={index} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {timeline && timeline.length > 0 ? (
                <div className="space-y-4">
                  {timeline.map((event, index) => (
                    <div
                      key={event.id}
                      className="relative pl-8 pb-4 last:pb-0"
                    >
                      {index !== timeline.length - 1 && (
                        <div className="absolute left-[11px] top-8 bottom-0 w-[2px] bg-border" />
                      )}
                      <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm">
                            {event.eventType}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {new Date(event.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No activity recorded yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          {/* Quick Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Quick Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Applied On</p>
                  <p className="font-medium">
                    {new Date(candidate.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="font-medium">
                    {new Date(candidate.updatedAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Candidate ID</p>
                  <p className="font-medium font-mono text-sm">
                    {candidate.id}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" variant="default">
                Schedule Interview
              </Button>
              <Button className="w-full" variant="outline">
                Send Email
              </Button>
              <Button className="w-full" variant="outline">
                Download CV
              </Button>
              <Button className="w-full" variant="destructive">
                Reject Candidate
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
